import { io } from 'socket.io-client';

class SocketManager {
    constructor() {
        this.socket = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 3;
        this.connectionListeners = new Set();
        this.isConnected = false;
    }

   connect() {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        
        if (!token) {
            console.error('No authentication token found');
            return;
        }

        // Fix: Use correct backend URL and port
        const backendUrl = window.location.hostname === 'localhost' 
            ? 'http://localhost:3001'  // Changed from 5000 to 3001
            : 'https://api.poslah.com';

        this.socket = io(backendUrl, {
            auth: {
            token: token
            },
            autoConnect: true,
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 2000,
            reconnectionAttempts: 5
        });
        this.socket.on('connect', () => {
            console.log('🔌 Socket connected successfully');
            this.isConnected = true;
            this.reconnectAttempts = 0;
            
            // Join user-specific room and vendor room
            this.joinUserRooms();
            
            // Notify all listeners about connection
            this.connectionListeners.forEach(callback => callback(true));
        });

        this.socket.on('connect_error', (error) => {
            console.error('❌ Socket connection error:', error.message);
            this.isConnected = false;
            
            if (error.message.includes('Token expired')) {
                this.handleTokenExpired();
            } else if (error.message.includes('Authentication')) {
                this.handleAuthError();
            }
            
            // Notify listeners about disconnection
            this.connectionListeners.forEach(callback => callback(false));
        });

        this.socket.on('disconnect', (reason) => {
            console.log('🔌 Socket disconnected:', reason);
            this.isConnected = false;
            
            // Notify listeners about disconnection
            this.connectionListeners.forEach(callback => callback(false));
        });

        // Set up notification listeners
        this.setupNotificationListeners();

        return this.socket;
    }

    joinUserRooms() {
        try {
            const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                
                // Join user-specific room
                if (user.id) {
                    this.socket.emit('join-room', `user-${user.id}`);
                }
                
                // Join vendor-specific room
                if (user.vendorId) {
                    this.socket.emit('join-room', `vendor-${user.vendorId}`);
                }
                
                // Join branch-specific room
                if (user.branchId) {
                    this.socket.emit('join-room', `branch-${user.branchId}`);
                }
                
                console.log('✅ Joined socket rooms for user:', user.username);
            }
        } catch (error) {
            console.error('❌ Error joining user rooms:', error);
        }
    }

    setupNotificationListeners() {
        if (!this.socket) return;

        // Listen for new notifications
        this.socket.on('new-notification', (notification) => {
            console.log('🔔 New notification received:', notification);
            
            // Trigger browser notification if permission granted
            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(notification.title, {
                    body: notification.message,
                    icon: '/favicon.ico'
                });
            }
            
            // Emit custom event for components to listen to
            window.dispatchEvent(new CustomEvent('new-notification', { 
                detail: notification 
            }));
        });

        // Listen for notification count updates
        this.socket.on('notification-counts-updated', (counts) => {
            console.log('📊 Notification counts updated:', counts);
            window.dispatchEvent(new CustomEvent('notification-counts-updated', { 
                detail: counts 
            }));
        });

        // Listen for menu list updates
        this.socket.on('menu-list-updated', (data) => {
            console.log('🍽️ Menu list updated:', data);
            window.dispatchEvent(new CustomEvent('menu-list-updated', { 
                detail: data 
            }));
        });

        // Listen for category list updates
        this.socket.on('category-list-updated', (data) => {
            console.log('📂 Category list updated:', data);
            window.dispatchEvent(new CustomEvent('category-list-updated', { 
                detail: data 
            }));
        });

        // Listen for order updates
        this.socket.on('order-list-updated', (data) => {
            console.log('📋 Order list updated:', data);
            window.dispatchEvent(new CustomEvent('order-list-updated', { 
                detail: data 
            }));
        });

        // Listen for vendor notifications
        this.socket.on('vendor-notification', (data) => {
            console.log('🏢 Vendor notification received:', data);
            window.dispatchEvent(new CustomEvent('vendor-notification', { 
                detail: data 
            }));
        });

        // Listen for branch notifications
        this.socket.on('branch-notification', (data) => {
            console.log('🏪 Branch notification received:', data);
            window.dispatchEvent(new CustomEvent('branch-notification', { 
                detail: data 
            }));
        });
    }

    // Add connection listener
    onConnectionChange(callback) {
        this.connectionListeners.add(callback);
        
        // Immediately call with current status
        callback(this.isConnected);
        
        // Return unsubscribe function
        return () => {
            this.connectionListeners.delete(callback);
        };
    }

    // Get connection status
    isSocketConnected() {
        return this.isConnected && this.socket?.connected;
    }

    // Request browser notification permission
    async requestNotificationPermission() {
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            console.log('📱 Notification permission:', permission);
            return permission === 'granted';
        }
        return false;
    }

    handleTokenExpired() {
        console.log('🔐 Token expired, redirecting to login...');
        this.redirectToLogin();
    }

    handleAuthError() {
        console.error('🔐 Authentication failed');
        this.redirectToLogin();
    }

    redirectToLogin() {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        sessionStorage.clear();
        
        // Redirect to login page
        if (window.location.pathname !== '/signin') {
            window.location.href = '/signin';
        }
    }

    disconnect() {
        if (this.socket) {
            console.log('🔌 Disconnecting socket...');
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
            
            // Notify listeners about disconnection
            this.connectionListeners.forEach(callback => callback(false));
        }
    }

    getSocket() {
        return this.socket;
    }

    // Emit custom events (for testing)
    emit(event, data) {
        if (this.socket && this.isConnected) {
            this.socket.emit(event, data);
        }
    }
}

export default new SocketManager();